import { showToast } from '../../../engine/toast';
import { adminGetUser, adminSetKycStatus, KYC_STATUSES, type AdminUser, type AdminUserDetail } from '../admin-api';
import { escapeHtml, when, INPUT_CLS, BTN_CLS } from '../admin-ui';
import type { PaneCtx } from '../panes/types';
import { errorText } from './fields';

const IMAGE_EXT = /\.(png|jpe?g|webp|gif|avif)$/i;

function row(label: string, value: string): string {
  return `
    <div class="flex gap-3 py-1.5 border-b border-border/60">
      <div class="w-32 shrink-0 text-[10px] text-text-dim uppercase tracking-wide pt-0.5">${escapeHtml(label)}</div>
      <div class="min-w-0 flex-1 text-sm text-text break-words">${escapeHtml(value || '—')}</div>
    </div>`;
}

/**
 * The submitted document.
 *
 * `kycDocumentUrl` is whatever extension the player uploaded — the submit
 * endpoint keeps the original one — so a PDF or a HEIC would render as a
 * broken image. Only known raster types are inlined; everything else gets a
 * link. Either way it opens in a new tab, because the file is served from
 * /uploads and leaves the SPA.
 */
function documentHtml(url: string | null): string {
  if (!url) return '<p class="text-xs text-text-dim">No document was uploaded.</p>';
  const safe = escapeHtml(url);
  if (IMAGE_EXT.test(url)) {
    return `
      <a href="${safe}" target="_blank" rel="noopener noreferrer" class="block">
        <img src="${safe}" alt="Submitted identity document"
          class="max-h-64 w-auto rounded border border-border bg-surface-2" />
      </a>
      <p class="mt-1 text-[10px] text-text-dim">Opens full size in a new tab.</p>`;
  }
  return `<a href="${safe}" target="_blank" rel="noopener noreferrer" class="text-gold text-sm underline">Open the uploaded document</a>`;
}

function bodyHtml(d: AdminUserDetail | null, user: AdminUser): string {
  if (!d) return '<p class="text-xs text-text-dim">Loading&hellip;</p>';
  return `
    <div class="flex flex-col gap-3">
      <div>
        ${row('Status', d.kycStatus)}
        ${row('Full name', d.kycFullName ?? '')}
        ${row('Date of birth', d.kycDateOfBirth ? String(d.kycDateOfBirth).slice(0, 10) : '')}
        ${row('Address', d.kycAddress ?? '')}
        ${row('Country', d.kycCountry ?? '')}
        ${row('Submitted', when(d.kycSubmittedAt))}
        ${row('Reviewed', when(d.kycReviewedAt))}
        ${d.kycRejectReason ? row('Reject reason', d.kycRejectReason) : ''}
      </div>

      <div>
        <h3 class="text-[10px] text-text-dim uppercase tracking-wide mb-1.5">Document</h3>
        ${documentHtml(d.kycDocumentUrl)}
      </div>

      <form id="kyc-decide" data-form="kyc-decide" class="flex flex-col gap-3 pt-2 border-t border-border">
        <h3 class="text-[10px] text-text-dim uppercase tracking-wide">Set status</h3>
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">Status</span>
          <select name="status" class="${INPUT_CLS}">
            ${KYC_STATUSES.map(
              (s) => `<option value="${s}" ${s === d.kycStatus ? 'selected' : ''}>${s}</option>`,
            ).join('')}
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">Reason (kept only when rejecting)</span>
          <input name="reason" type="text" value="${escapeHtml(d.kycRejectReason ?? '')}" class="${INPUT_CLS}" />
        </label>
        <div class="flex justify-end gap-2">
          <button type="button" data-action="close" class="min-h-11 px-4 text-sm text-text-dim hover:text-text touch-manipulation">Close</button>
          <button type="submit" class="${BTN_CLS}">Save status</button>
        </div>
      </form>

      <p class="text-[10px] text-text-dim leading-relaxed">
        Any status can be set from any other — unlike the approve/reject flow, which only accepts a pending
        submission. Setting a status here does not email ${escapeHtml(user.email)}.
      </p>
    </div>`;
}

/**
 * Review a player's KYC submission and set the status.
 *
 * The detail comes from `GET /admin/users/:id`: core's `/admin/kyc/users`
 * list leaves out the address, date of birth and document URL, so it cannot
 * back a review screen on its own.
 */
export function openKycReview(ctx: PaneCtx, user: AdminUser): void {
  let detail: AdminUserDetail | null = null;

  const handle = ctx.openModal({
    title: 'KYC review',
    subtitle: user.username ?? user.email,
    size: 'lg',
    body: bodyHtml(null, user),

    onSubmit(kind, form, h) {
      if (kind !== 'kyc-decide') return;
      const data = new FormData(form);
      const status = String(data.get('status') ?? '');
      const reason = String(data.get('reason') ?? '').trim();
      h.setBusy(true);
      void (async () => {
        try {
          await adminSetKycStatus(user.id, status, reason || undefined);
          showToast(`KYC set to ${status}.`, 'success');
          h.close();
          await ctx.reload('players');
        } catch (err) {
          h.setError(errorText(err, 'Could not set that status.'));
          h.setBusy(false);
        }
      })();
    },
  });

  // Rendered once with a placeholder, then the body is swapped in when the
  // detail lands. Safe because nothing is focusable in the placeholder — the
  // form only exists after this replacement.
  void (async () => {
    try {
      detail = await adminGetUser(user.id);
      if (ctx.disposed()) return;
      const body = handle.el.querySelector('[data-modal-body]');
      if (body) body.innerHTML = bodyHtml(detail, user);
    } catch (err) {
      handle.setError(errorText(err, 'Could not load that submission.'));
    }
  })();
}
